import {WriteButton} from '../../../common/buttons/WriteActions';
import {useDelayedNameError, nameErrorHelperSx} from '../../../../hooks/useDelayedNameError';
import {validateCatalogName} from '../../../../common/catalogNameValidation';
import {mobileDialogSx} from '../../../common/dialogs/mobileDialogSx';
import {useEffect, useState} from 'react';
import {Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import {PackageGuidDto} from '../../../../models/Packages/IPackage';
import {packageAPI} from '../../../../services/PackageServices';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {getApiErrorMessage, getApiFieldErrors} from '../../../../common/apiErrors';
import {DeletePackageDialog} from './DeletePackageDialog';

type Props = { pack: PackageGuidDto | undefined; open: boolean; onClose: () => void; };
export const EditPackageDialog = ({pack, open, onClose}: Props) => {
    const {t} = useAppTranslation();
    const {data: existingNames, isFetching: namesFetching, error: namesError} = packageAPI.useGetAvailablePackageNamesQuery(undefined, {skip: !open, refetchOnMountOrArgChange: true});
    const [name, setName] = useState(pack?.name ?? '');
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [updatePackage, {isLoading, error}] = packageAPI.useUpdatePackageMutation();
    const [deletePackageRequest, {isLoading: isDeleting}] = packageAPI.useDeletePackageMutation();
    useEffect(() => setName(pack?.name ?? ''), [pack]);
    const nameValidation = validateCatalogName(name, existingNames, pack?.name);
    const nameError = useDelayedNameError(name, nameValidation === 'too_short' ? t('common:name_min_length') : nameValidation === 'duplicate' ? t('items:package_name_exists') : '');
    const nameInvalid = !!nameValidation || !existingNames || namesFetching || !!namesError;
    const submit = async () => {
        if (!pack || nameInvalid || isLoading || isDeleting) return;
        if (!pack.id) return;
        await updatePackage({id: pack.id, body: {id: pack.id, name: name.trim(), unit: pack.unit, stocks: [], productTypes: pack.productTypes}}).unwrap();
        onClose();
    };
    const openDelete = () => {
        setDeleteError('');
        setDeleteOpen(true);
    };
    const deletePackage = async () => {
        if (!pack?.id) return false;
        setDeleteError('');
        try {
            await deletePackageRequest(pack.id).unwrap();
            onClose();
            return true;
        } catch (requestError) {
            setDeleteError(getApiErrorMessage(requestError, t));
            return false;
        }
    };
    return <Dialog sx={mobileDialogSx} open={open} onClose={isLoading || isDeleting ? undefined : onClose} fullWidth maxWidth="xs" PaperProps={{sx: {borderRadius: 3}}}>
        <DialogTitle>{t('items:edit_package')}</DialogTitle><DialogContent><Stack spacing={2} sx={{pt: 1}}>{namesError && <Alert severity="error">{getApiErrorMessage(namesError, t)}</Alert>}{error && !getApiFieldErrors(error, t).name && <Alert severity="error">{getApiErrorMessage(error, t)}</Alert>}
            <TextField autoFocus required fullWidth label={t('items:item_name')} value={name} onChange={(event) => setName(event.target.value)} error={!!nameError || !!getApiFieldErrors(error, t).name} helperText={nameError || getApiFieldErrors(error, t).name || ' '} FormHelperTextProps={{sx: nameErrorHelperSx}}/>
        </Stack></DialogContent><DialogActions sx={{px: 3, pb: 2}}><WriteButton color="error" onClick={openDelete} disabled={!pack || isLoading || isDeleting} startIcon={<DeleteIcon/>}>{t('common:delete')}</WriteButton><Button onClick={onClose} disabled={isLoading || isDeleting}>{t('common:cancel')}</Button><WriteButton variant="contained" onClick={submit} disabled={nameInvalid || isLoading || isDeleting} startIcon={<SaveIcon/>}>{t('common:save')}</WriteButton></DialogActions>
        {pack && <DeletePackageDialog open={deleteOpen} onClose={() => setDeleteOpen(false)} name={pack.name} onDelete={deletePackage} error={deleteError}/>}
    </Dialog>;
};
